import Fuse from "fuse.js";
import type { FuseResult, IFuseOptions } from "fuse.js";
import { BmuModel } from "models/bmuData/bmuModel";
import {
  GetOptionLabel,
  GetOptionValue,
} from "react-select/dist/declarations/src/types";

export interface BmuOption {
  nationalGridBmuName: string;
  elexonBmuId: string;
  leadPartyName: string;
  bmuType: string;
  fuelType: string;
}

export type IsMulti = false;

export const getOptionLabel: GetOptionLabel<BmuOption> = (option) =>
  option.elexonBmuId;

export const getOptionValue: GetOptionValue<BmuOption> = (option) =>
  option.nationalGridBmuName;

export const mapModelsToOptions = (rawData: BmuModel[] | null): BmuOption[] =>
  rawData?.map((b) => ({
    elexonBmuId: b.elexonBmUnit ?? "—",
    nationalGridBmuName: b.nationalGridBmUnit,
    leadPartyName: b.leadPartyName ?? "—",
    bmuType: b.bmUnitType ?? "—",
    fuelType: b.fuelType ?? "—",
  })) ?? [];

const keys: (keyof BmuOption)[] = [
  "nationalGridBmuName",
  "elexonBmuId",
  "leadPartyName",
];

export const fuzzyBmuFilter = (
  allOptions: BmuOption[],
  filterString: string
): BmuOption[] => {
  if (!filterString) {
    return allOptions;
  }
  const config: IFuseOptions<BmuOption> = {
    /**
     * Note on threshold: this mainly affects the scoring algorithm and the output score (see https://www.fusejs.io/concepts/scoring-theory.html).
     * It also seems to somewhat filters the results, but not to the same actual threshold; there will be results with a score > 0.2 in the output.
     * Through testing, 0.3 seems to be about right, where lots of relevant results are included.
     */
    threshold: 0.3,
    includeMatches: true, // This will allow for future highlighting of matches within BMU data (e.g. in bold in the Figma designs)
    ignoreLocation: true, // Default is to match from the start, we don't care where the search result appears and want a substring match
    keys,
  };
  const fuse = new Fuse(allOptions, config);
  return fuse
    .search(filterString)
    .map((result: FuseResult<BmuOption>) => result.item);
};
